/**
 * ChatPage — replaces MessageCtrl + templates/chat.html.
 *
 * AngularJS → React mapping:
 *   $scope.messages            → useMessages() hook
 *   $scope.send()              → handleSend() + socket.send('message', data)
 *   $scope.reload()            → fetchMessages()
 *   $scope.myId                → driver.id from useAuth()
 *   on-hold (clipboard copy)   → long-press via pointer events + @capacitor/clipboard
 *   autolinker directive       → <MessageText> links bare http(s) URLs
 *   nl2br filter               → <MessageText> turns newlines into <br>; office HTML is
 *                                rebuilt from an allow-list of formatting tags
 *   timeAgo filter             → simple relative time via Intl.RelativeTimeFormat
 *   $cordovaNativeAudio.play() → playMessageSound() same as DashboardPage
 *   $rootScope.$on('MessageReceived') → useSocketEvent('driver', ...)
 *
 * One layout in every Size class. The compose box sits under the header and the newest
 * message is first, so the on-screen keyboard never covers the input and a reply shows
 * where the driver is already looking. Only the header differs between Size classes:
 * Back and Reload are icons in Single-pane and labelled buttons in Split.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Group, Paper, Stack, Text, Textarea, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconRefresh, IconSend } from '@tabler/icons-react';
import { HeaderAction } from '@/components/HeaderAction';
import { MessageText } from '@/components/MessageText';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket, useSocketEvent } from '@/contexts/SocketContext';
import { useBackToDashboard } from '@/hooks/useBackToDashboard';
import { useMessages } from '@/hooks/useMessages';
import { useSizeClass } from '@/hooks/useSizeClass';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import type { Message } from '@/types';

const LONG_PRESS_MS = 600;

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(ts: string): string {
  try {
    const ms = isNaN(Number(ts)) ? Date.parse(ts) : Number(ts) * 1000;
    const diff = (ms - Date.now()) / 1000;
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    const abs = Math.abs(diff);
    if (abs < 60) return rtf.format(Math.round(diff), 'second');
    if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
    return rtf.format(Math.round(diff / 86400), 'day');
  } catch {
    return ts;
  }
}

function playMessageSound(): void {
  new Audio('/mp3/message_ding.mp3').play().catch(() => undefined);
}

async function copyText(text: string): Promise<void> {
  try {
    const { Clipboard } = await import('@capacitor/clipboard');
    await Clipboard.write({ string: text });
  } catch {
    // The plugin failed, so try the browser's own clipboard before giving up.
    await navigator.clipboard.writeText(text);
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ChatPage() {
  const { driver } = useAuth();
  const { send } = useSocket();
  const { pathname } = useLocation();
  const handleBack = useBackToDashboard();
  const { sizeClass, shortHeight } = useSizeClass();
  const visualViewport = useVisualViewport();
  const { messages, loading, fetchMessages, addMessage } = useMessages();
  const [msgText, setMsgText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const myId = `driver${driver?.id ?? ''}`;

  // AppShell keeps this page mounted and hidden while the Dashboard shows. A keyboard
  // that opens there belongs to the Dashboard, so only the visible page follows it.
  const followsKeyboard = pathname === '/chat';
  const iconOnly = sizeClass === 'single';
  // Short height tightens the spacing around the header and the compose box only.
  const spacing = shortHeight ? 4 : 'xs';

  useEffect(() => {
    void fetchMessages();
  }, [fetchMessages]);

  // Incoming socket messages
  useSocketEvent<Message>('driver', data => {
    if (data.to_id === myId || data.from_id === myId) {
      addMessage(data);
      playMessageSound();
    }
  });

  const handleSend = () => {
    if (!msgText.trim() || !driver) return;

    const msg: Message = {
      from_id: myId,
      to_id: 'office',
      from_name: `${driver.firstname} ${driver.lastname}`,
      to_name: 'Office',
      message: msgText,
      timestamp: String(Math.floor(Date.now() / 1000)),
    };

    send('message', msg);
    addMessage(msg);
    setMsgText('');
    // Closes the on-screen keyboard, so the driver sees the history with the message in it.
    textareaRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Long-press to copy (replaces on-hold + cordova-clipboard)
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyMessage = async (text: string): Promise<void> => {
    try {
      await copyText(text);
      notifications.show({ color: 'green', message: 'Copied' });
    } catch {
      // Copying is best effort. A blocked clipboard copies nothing and shows nothing.
    }
  };
  const cancelPress = () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
  };
  const startPress = (text: string) => {
    // A second finger starts a second press, and only one timer can be cancelled.
    cancelPress();
    pressTimerRef.current = setTimeout(() => void copyMessage(text), LONG_PRESS_MS);
  };
  useEffect(
    () => () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    },
    [],
  );

  return (
    <Stack
      // The page takes the visual viewport's top and height, so the history ends at the
      // on-screen keyboard. The shell around it has already taken the safe-area insets
      // off its own height, and the page never grows past the shell.
      h={
        followsKeyboard
          ? `calc(${visualViewport.height}px - env(safe-area-inset-top) - env(safe-area-inset-bottom))`
          : '100%'
      }
      mah="100%"
      pos="relative"
      top={followsKeyboard ? visualViewport.offsetTop : 0}
      gap={spacing}
      px="md"
      py={spacing}
    >
      {/* Header */}
      <Group component="header" gap="xs" wrap="nowrap">
        <HeaderAction label="Back" icon={IconArrowLeft} iconOnly={iconOnly} onClick={handleBack} />
        <Title order={5} flex={1} miw={0} lineClamp={1}>
          Office Chat
        </Title>
        <HeaderAction
          label="Reload"
          icon={IconRefresh}
          iconOnly={iconOnly}
          loading={loading}
          onClick={() => void fetchMessages()}
        />
      </Group>

      {/* Compose */}
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Textarea
          ref={textareaRef}
          flex={1}
          placeholder="Type a message…"
          value={msgText}
          onChange={e => setMsgText(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          enterKeyHint="send"
          autosize
          minRows={1}
          maxRows={4}
        />
        <Button
          leftSection={<IconSend size={20} />}
          onClick={handleSend}
          disabled={!msgText.trim()}
        >
          Send
        </Button>
      </Group>

      {/* Message history */}
      <Box role="log" aria-label="Messages" flex={1} mih={0} style={{ overflowY: 'auto' }}>
        <Stack gap="xs">
          {[...messages]
            .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
            .map((msg, idx) => {
              const isMine = myId === msg.from_id;
              return (
                <Box
                  key={idx}
                  style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}
                >
                  <Paper
                    shadow="xs"
                    p="xs"
                    bg={isMine ? 'blue.1' : 'gray.1'}
                    style={{
                      borderRadius: 12,
                      maxWidth: 'min(80%, 600px)',
                      // The browser's text selection and the iOS callout would compete
                      // with the long-press. iOS Safari needs the prefixed property.
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      WebkitTouchCallout: 'none',
                    }}
                    onPointerDown={() => startPress(msg.message)}
                    onPointerUp={cancelPress}
                    onPointerLeave={cancelPress}
                    // A touch scroll ends the press with pointercancel, not pointerup.
                    onPointerCancel={cancelPress}
                  >
                    <Text
                      fz="md"
                      component="div"
                      data-testid="chat-message-text"
                      // A long link has no space to break at and would widen the bubble.
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      <MessageText html={msg.message} />
                    </Text>
                    <Text fz="xs" c="dimmed" mt={2}>
                      {msg.from_name}, {relativeTime(msg.timestamp)}
                    </Text>
                  </Paper>
                </Box>
              );
            })}
        </Stack>
      </Box>
    </Stack>
  );
}
